import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LuMessageSquare, LuSparkles, LuUser, LuSend, LuLoaderCircle, LuX } from 'react-icons/lu';
import { sendOllamaChatStream } from '../../api/ollama';
import { useAuth } from '../../context/AuthContext';
import { getPageMeta } from '../../pages/pageMeta';

const markdownComponents: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  a: ({ href, children, ...props }) => {
    const safe = href && (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('/'));
    if (safe) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:opacity-80" {...props}>
          {children}
        </a>
      );
    }
    return <span {...props}>{children}</span>;
  },
  ul: ({ children, ...props }) => <ul className="my-1.5 list-disc pl-5 space-y-0.5" {...props}>{children}</ul>,
  ol: ({ children, ...props }) => <ol className="my-1.5 list-decimal pl-5 space-y-0.5" {...props}>{children}</ol>,
  li: ({ children, ...props }) => <li className="leading-relaxed" {...props}>{children}</li>,
  p: ({ children, ...props }) => <p className="my-1.5 first:mt-0 last:mb-0 leading-relaxed" {...props}>{children}</p>,
  h1: ({ children, ...props }) => <h3 className="mt-2.5 mb-1 font-bold text-gray-800 dark:text-slate-100" {...props}>{children}</h3>,
  h2: ({ children, ...props }) => <h3 className="mt-2 mb-1 font-semibold text-gray-800 dark:text-slate-100" {...props}>{children}</h3>,
  h3: ({ children, ...props }) => <h4 className="mt-1.5 mb-0.5 font-semibold text-gray-800 dark:text-slate-100" {...props}>{children}</h4>,
  strong: ({ children, ...props }) => <strong className="font-semibold" {...props}>{children}</strong>,
  em: ({ children, ...props }) => <em className="italic" {...props}>{children}</em>,
  code: ({ className, children, ...props }) =>
    className ? (
      <code className="block text-xs" {...props}>{children}</code>
    ) : (
      <code className="rounded bg-gray-100 px-1 py-0.5 text-xs dark:bg-stone-700" {...props}>{children}</code>
    ),
  pre: ({ children, ...props }) => <pre className="my-1.5 overflow-x-auto rounded-md bg-gray-100 p-2 text-xs dark:bg-stone-700" {...props}>{children}</pre>,
  blockquote: ({ children, ...props }) => <blockquote className="my-1.5 border-l-4 border-primary/50 pl-3 italic text-gray-600 dark:text-slate-400" {...props}>{children}</blockquote>,
};

type IconProps = { className?: string; 'aria-hidden'?: boolean };
const MsgIcon = LuMessageSquare as React.ComponentType<IconProps>;
const SparkIcon = LuSparkles as React.ComponentType<IconProps>;
const UserIcon = LuUser as React.ComponentType<IconProps>;
const SendIcon = LuSend as React.ComponentType<IconProps>;
const LoadIcon = LuLoaderCircle as React.ComponentType<IconProps>;
const CloseIcon = LuX as React.ComponentType<IconProps>;

const PRODUCT_CONTEXT =
  'ClickLaw is an AI legal practice management system. Main features: Cases, Clients, Documents, Tasks, Calendar, Billing, Reports, Settings, Profile. Guide users on how to use these. Be concise and friendly. When helpful, use markdown: lists, **bold**, [links](url), and headings (##, ###).';

const PAGE_CONTENT_MAX = 3200;

function getPageContentExcerpt(): string {
  const el = document.querySelector('main') || document.querySelector('[role="main"]');
  if (!el) return '';
  const raw = (el as HTMLElement).innerText || '';
  return raw.replace(/\s+/g, ' ').trim().slice(0, PAGE_CONTENT_MAX);
}

type MessageRole = 'user' | 'assistant';
interface ChatMessage {
  role: MessageRole;
  content: string;
}

const INITIAL_MESSAGES: ChatMessage[] = [
  { role: 'assistant', content: 'Hi, how can I help you today?' },
];

/** Document/Legal Forms viewer pages have their own sidebar ClickCompanion; hide floating FAB there. */
function hideFabOnViewerPath(pathname: string): boolean {
  if (pathname.startsWith('/dashboard/subscription')) return true;
  return (
    /^\/dashboard\/documents\/[^/]+$/.test(pathname) ||
    /^\/documents\/[^/]+$/.test(pathname) ||
    /^\/dashboard\/legal-forms\/file\/[^/]+$/.test(pathname) ||
    /^\/legal-forms\/file\/[^/]+$/.test(pathname)
  );
}

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(INITIAL_MESSAGES);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { user } = useAuth();

  const context = useMemo(() => {
    const { section, title } = getPageMeta(location.pathname);
    const page = `Current page: ${section} — ${title}.`;
    const userLine = user ? `Logged-in user: ${user.name} (${user.email}).` : 'User is not signed in.';
    return [PRODUCT_CONTEXT, page, userLine].join('\n');
  }, [location.pathname, user]);

  // Scroll to bottom when panel opens
  useEffect(() => {
    if (isOpen) {
      const el = scrollRef.current;
      if (el) {
        setTimeout(() => {
          el.scrollTop = el.scrollHeight;
        }, 100);
      }
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const content = input.trim();
    if (!content || isLoading) return;

    const userMessage: ChatMessage = { role: 'user', content };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setError(null);
    setIsLoading(true);

    const pageExcerpt = getPageContentExcerpt();
    const contextWithPage = pageExcerpt
      ? `${context}\n\nPage content (excerpt):\n${pageExcerpt}`
      : context;

    const apiMessages = [
      ...messages.map((m) => ({ role: m.role, content: m.content })),
      { role: 'user' as const, content },
    ];

    // Stream into an assistant message
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    try {
      await sendOllamaChatStream(apiMessages, contextWithPage, (token) => {
        setMessages((prev) => {
          const n = [...prev];
          const last = n[n.length - 1];
          if (last?.role === 'assistant') {
            n[n.length - 1] = { ...last, content: last.content + token };
          }
          return n;
        });
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(msg);
      setMessages((prev) => {
        const n = [...prev];
        const last = n[n.length - 1];
        if (last?.role === 'assistant' && !last.content) {
          n[n.length - 1] = { ...last, content: 'Sorry, something went wrong. Please try again.' };
        }
        return n;
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (hideFabOnViewerPath(location.pathname)) return null;

  return (
    <>
      <style>{`
        @keyframes chat-attract {
          0%, 100% { transform: scale(1); box-shadow: 0 4px 14px 0 rgba(0,0,0,0.2); }
          50% { transform: scale(1.08); box-shadow: 0 8px 24px 4px rgba(6,57,50,0.45); }
        }
        .animate-chat-fab {
          animation: chat-attract 2.2s ease-in-out infinite;
        }
        .animate-chat-fab:hover {
          animation-play-state: paused;
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-chat-fab { animation: none; }
        }
      `}</style>
      {/* FAB */}
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((o) => !o)}
        className={`fixed bottom-4 right-4 z-[100] inline-flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-gray-200 bg-primary p-0 font-medium text-white shadow-md transition-transform hover:scale-105 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:border-gray-700 dark:bg-primary-700 dark:hover:bg-primary-800 sm:h-14 sm:w-14 ${!isOpen ? 'animate-chat-fab' : ''}`}
      >
        {!isOpen && (
          <span className="pointer-events-none absolute inset-0 rounded-full bg-primary animate-ping opacity-25 motion-reduce:animate-none" aria-hidden />
        )}
        <MsgIcon className="relative z-10 text-lg sm:text-xl" aria-hidden />
      </button>

      {/* Panel */}
      {isOpen && (
        <div
          className="fixed left-4 right-4 bottom-16 z-[100] flex min-h-[300px] max-h-[calc(100dvh-6rem)] flex-col overflow-hidden rounded-2xl border border-gray-200/70 shadow-lg backdrop-blur-xl backdrop-saturate-150 bg-white/75 dark:border-stone-600/50 dark:bg-stone-800/75 sm:left-auto sm:right-0 sm:mr-4 sm:bottom-[calc(3.5rem+1.5rem)] sm:min-h-0 sm:max-h-[634px] sm:h-[634px] sm:w-[440px] sm:max-w-[440px] sm:rounded-lg"
          style={{ boxShadow: '0 0 #0000, 0 0 #0000, 0 1px 2px 0 rgb(0 0 0 / 0.05)' }}
        >
          {/* Header */}
          <div className="relative shrink-0 border-b border-gray-200/70 px-4 py-3 pr-12 dark:border-stone-600/50 sm:px-6 sm:pb-6 sm:pt-6 sm:pr-12">
            <h2 className="flex items-center gap-2 text-base font-semibold tracking-tight text-gray-900 dark:text-slate-100 sm:text-lg">
              <img src="/ICON-GREEN.png" alt="" className="h-6 w-auto sm:h-7" aria-hidden />
              Click Companion
            </h2>
            <p className="text-xs text-gray-500 dark:text-slate-400 sm:text-sm sm:leading-3">What do you want to do today?</p>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Close chatbot"
              className="absolute right-3 top-3 rounded-full p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary dark:text-slate-400 dark:hover:bg-stone-700 dark:hover:text-slate-200 sm:right-4 sm:top-4"
            >
              <CloseIcon className="h-5 w-5" aria-hidden />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="clicklaw-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pb-2 pr-2 sm:px-4 sm:pr-4 sm:pb-4"
          >
            {messages.map((m, i) => {
              const isLast = i === messages.length - 1;
              const showStreamingPlaceholder = isLast && m.role === 'assistant' && !m.content && isLoading;
              const displayContent = m.content || (showStreamingPlaceholder ? '...' : '');
              return (
                <div key={i} className="my-3 flex gap-2 text-sm text-gray-600 dark:text-slate-300 sm:my-4 sm:gap-3">
                  <span className="relative flex h-8 w-8 shrink-0 overflow-hidden rounded-full">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-gray-100 dark:border-gray-600 dark:bg-stone-700">
                      {m.role === 'assistant' ? (
                        showStreamingPlaceholder ? (
                          <LoadIcon className="h-5 w-5 animate-spin text-gray-700 dark:text-slate-200" aria-hidden />
                        ) : (
                          <SparkIcon className="h-5 w-5 text-gray-700 dark:text-slate-200" aria-hidden />
                        )
                      ) : (
                        <UserIcon className="h-5 w-5 text-gray-700 dark:text-slate-200" aria-hidden />
                      )}
                    </div>
                  </span>
                  <div className="min-w-0 flex-1 break-words leading-relaxed">
                    <span className="block font-bold text-gray-700 dark:text-slate-200">{m.role === 'assistant' ? 'AI' : 'You'}</span>
                    <div className="chat-message-body [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {displayContent}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              );
            })}
            {error && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400" role="alert">
                {error}
              </p>
            )}
          </div>

          {/* Input */}
          <div className="shrink-0 border-t border-gray-200/70 dark:border-stone-600/50">
            <form onSubmit={handleSubmit} className="flex w-full items-center gap-2 p-3 sm:p-4">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message"
                disabled={isLoading}
                className="min-w-0 flex-1 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder-gray-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-stone-700 dark:text-slate-100 dark:placeholder-slate-400"
              />
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:bg-primary-700 dark:hover:bg-primary-800 sm:gap-2 sm:px-4"
              >
                <SendIcon className="h-5 w-5 shrink-0" aria-hidden />
                Send
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
